import React from 'react';
import {ILogFilters, ILogFilterFormType, convertToLogFilter} from 'types/diplomat/logs/LogFilter';
import {CloudTransport} from 'types/transport/Transport';
import {useFetchLogsView} from './hooks';
import Dialog from 'components/Dialog';
import AsyncContainer from 'components/AsyncContainer';
import {useTranslation} from 'react-i18next';
import {LogText} from './LogText';
import {DialogActions, Grid, Typography} from '@mui/material';
import Button from 'components/Button';
import {useModal} from 'common/hooks/useModal';
import {LogFilterDialog} from './LogFilterDialog';
import {LogLevelLogFilter} from 'types/diplomat/logs/LogLevelLogFilter';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {useDownloadLog} from './useDownloadLog';

type Props = {
   logNames?: string[];
   transport?: CloudTransport;
   logFilters?: ILogFilters;
   onClose: () => void;
};

export const LogViewerDialog = React.memo((props: Props) => {
   const {t} = useTranslation();
   const {logNames, transport, logFilters, onClose} = props;
   const [filters, setFilters] = React.useState<ILogFilters | undefined>(logFilters);

   const {fetch, state, loading} = useFetchLogsView({logNames, transport, logFilters: filters});

   React.useEffect(() => {
      fetch();
   }, [fetch]);

   const {open, handleClose, handleOpen} = useModal(false);
   const onFilterDialogClose = React.useCallback(
      (filter?: ILogFilterFormType) => {
         handleClose();
         if (filter) {
            const newFilters = convertToLogFilter(filter);
            // console.debug(newFilters);
            setFilters(newFilters);
         }
      },
      [setFilters, handleClose]
   );

   const resetFilter = React.useCallback(() => {
      const newFilters = [LogLevelLogFilter.cast({})];
      setFilters(newFilters);
   }, [setFilters]);

   const downlaodLog = useDownloadLog(state, !!transport);

   return (
      <Dialog
         maxWidth={'lg'}
         open={true}
         close={onClose}
         header={transport ? t('Diplomat Remote Agent Log Viewer') : t('Diplomat MFT Log Viewer')}
      >
         <AsyncContainer loading={loading}>
            {state && state.numLines ? (
               <LogText logView={state} isCloud={!!transport} />
            ) : (
               <Typography>{t('Log is empty')}</Typography>
            )}
         </AsyncContainer>
         <DialogActions>
            <Grid container spacing={3} justifyContent="center">
               <Grid item>
                  <Button onClick={downlaodLog}>{t('Save Log File')}</Button>
               </Grid>
               <Grid item>
                  <PrimaryButton onClick={handleOpen}>{t('Set Filter')}</PrimaryButton>
               </Grid>
               <Grid item>
                  <Button onClick={resetFilter}>{t('Reset Filter')}</Button>
               </Grid>
               <Grid item>
                  <PrimaryButton onClick={() => fetch()}>{t('Refresh')}</PrimaryButton>
               </Grid>
               <Grid item>
                  <Button onClick={onClose}>{t('Done')}</Button>
               </Grid>
            </Grid>
         </DialogActions>
         {open ? (
            <LogFilterDialog open={open} onClose={onFilterDialogClose} filter={filters} />
         ) : null}
      </Dialog>
   );
});
